'use client'

import type { ComponentProps, ReactNode } from 'react'
import type { SourceData } from '@/components/ui/source-accordion'
import type { ChartSeries } from '@/components/charts/chart-parts'

import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input, InputGroupSearch } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Separator } from '@/components/ui/separator'
import { MetricBadgeCount } from '@/components/ui/metrics'
import { FieldInline, FieldLabel } from '@/components/ui/field'
import { SourceList } from '@/components/ui/source-accordion'
import { Avatar, AvatarFallback, AvatarGroup, AvatarImage } from '@/components/ui/avatar'
import { avatarPhoto } from '@/constants/avatars'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AxisLabel, ChartCardFooter, LegendItem, StackedTrack } from '@/components/charts/chart-parts'
import { SourceIcon, sourceKey } from '@/components/icons/source-icons'
import { SubscriberMessengerIcon, UserMessengerIcon } from '@/components/icons/goodunited-icons'
import { ACQUISITION_CHANNELS } from '@/constants/chart-data'

import {
    CaretDownIcon,
    ClockIcon,
    CrownIcon,
    DownloadSimpleIcon,
    PaperPlaneRightIcon,
    UserIcon
} from '@/components/icons/icon-set'

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from '@/components/ui/select'

/*
 * GoodUnited / Drawer content blocks. Figma: 22 / Blocks / Drawers and 31 / Templates / Drawers.
 * Reusable parts: DrawerMetric (MetricCard/Compact), DrawerFieldRow (Field/Row), SupporterRow,
 * MessageRow, DrawerListSection, PreviewRegion, ActivityComment, CommentComposer and the footer recipes.
 * Content recipes plug into SheetBody; the shell (SheetHeader, SheetFooter) stays unchanged.
 */

/* MetricCard/Compact: a label and a Poppins / text-xl / semibold value, with optional people. */
export function DrawerMetric({ label, value, people, className, ...props }: Omit<ComponentProps<'div'>, 'children'> & { label: ReactNode, value: ReactNode, people?: number }) {
    return (
        <div data-slot='drawer-metric' className={cn('flex min-w-0 items-center justify-between gap-3 rounded-lg border border-border-default bg-surface-raised p-3', className)} {...props}>
            <div className='flex min-w-0 flex-col gap-1'>
                <span className='truncate text-sm text-text-primary'>
                    {label}
                </span>
                <span className='font-heading text-xl font-semibold text-text-primary'>
                    {value}
                </span>
            </div>
            {people !== undefined && <AvatarGroup count={people} size='sm' />}
        </div>
    )
}

/* Field/Row: a 112px label column beside its control. */
export function DrawerFieldRow({ label, htmlFor, children, className }: { label: string, htmlFor?: string, children: ReactNode, className?: string }) {
    return (
        <FieldInline className={className}>
            <FieldLabel htmlFor={htmlFor} className='py-2 text-base font-normal'>
                {label}
            </FieldLabel>
            <div className='min-w-0'>
                {children}
            </div>
        </FieldInline>
    )
}

/* Subscriber tag: Messenger bolt with Subscriber (Colors/external/messenger) or Not connected. */
export function SubscriberTag({ connected = true }: { connected?: boolean }) {
    return (
        <span className={cn('inline-flex items-center gap-1 text-xs', connected ? 'text-external-messenger' : 'text-text-secondary')}>
            <SubscriberMessengerIcon className='size-3.5' />
            {connected ? 'Subscriber' : 'Not connected'}
        </span>
    )
}

interface SupporterChild {
    name: string
    amount: string
    share: string
    connected?: boolean
}

interface SupporterRowProps {
    name: string
    amount: string
    detail?: string
    connected?: boolean
    rows?: SupporterChild[]
    defaultOpen?: boolean
}

/* SupporterRow: avatar, name with subscriber tag, supporting text and amount. Rows with donors expand. */
export function SupporterRow({ name, amount, detail, connected = true, rows, defaultOpen = false }: SupporterRowProps) {
    const [open, setOpen] = useState(defaultOpen)
    const expandable = !!rows?.length

    const content = (
        <>
            <Avatar size='lg'>
                <AvatarImage src={avatarPhoto(name)} alt='' />
                <AvatarFallback />
            </Avatar>
            <span className='flex min-w-0 flex-1 flex-col'>
                <span className='flex min-w-0 flex-wrap items-center gap-x-2'>
                    <span className='truncate text-base font-medium text-text-primary'>
                        {name}
                    </span>
                    <SubscriberTag connected={connected} />
                </span>
                {detail && (
                    <span className='text-sm text-text-secondary'>
                        {detail}
                    </span>
                )}
            </span>
            <span className='text-base font-medium text-text-primary tabular-nums'>
                {amount}
            </span>
        </>
    )

    return (
        <div data-slot='supporter-row' className='border-b border-border-subtle last:border-b-0'>
            {expandable
                ? (
                    <button
                        type='button'
                        onClick={() => setOpen(!open)}
                        aria-expanded={open}
                        className='flex w-full cursor-pointer items-center gap-3 px-1 py-3 text-left outline-none hover:bg-surface-subtle focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-border-focus'
                    >
                        {content}
                        <CaretDownIcon className={cn('size-4 shrink-0 text-icon-secondary transition-transform', open && 'rotate-180')} aria-hidden='true' />
                    </button>
                )
                : (
                    <div className='flex items-center gap-3 px-1 py-3'>
                        {content}
                        <span className='size-4 shrink-0' aria-hidden='true' />
                    </div>
                )}
            {expandable && open && (
                <div className='flex flex-col bg-surface-subtle'>
                    {rows.map(row => (
                        <div key={row.name} className='flex items-center gap-3 border-t border-border-subtle py-2 pr-1 pl-4'>
                            <Avatar size='default'>
                                <AvatarImage src={avatarPhoto(row.name)} alt='' />
                                <AvatarFallback />
                            </Avatar>
                            <span className='flex min-w-0 flex-1 flex-wrap items-center gap-x-2'>
                                <span className='truncate text-sm font-medium text-text-primary'>
                                    {row.name}
                                </span>
                                <SubscriberTag connected={row.connected ?? true} />
                            </span>
                            <span className='text-sm font-medium text-text-primary tabular-nums'>
                                {row.amount}
                            </span>
                            <span className='rounded-sm bg-surface-muted px-1 text-xs font-medium text-text-secondary'>
                                {row.share}
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}

interface MessageRowProps {
    name: string
    date: string
    message: string
}

/* Supporter message: avatar with the Messenger badge, name, date, message and a Reply action. */
export function MessageRow({ name, date, message }: MessageRowProps) {
    return (
        <div data-slot='message-row' className='flex items-center gap-3 border-b border-border-subtle py-3 last:border-b-0'>
            <span className='relative shrink-0'>
                <Avatar size='lg'>
                    <AvatarImage src={avatarPhoto(name)} alt='' />
                    <AvatarFallback />
                </Avatar>
                <span className='absolute -top-1 -right-1 inline-flex size-4 items-center justify-center rounded-full bg-surface-raised text-external-messenger [&_svg]:size-3'>
                    <UserMessengerIcon />
                </span>
            </span>
            <span className='flex min-w-0 flex-1 flex-col gap-0.5'>
                <span className='flex flex-wrap items-center gap-x-1.5 text-sm'>
                    <SubscriberMessengerIcon className='size-3.5 text-external-messenger' />
                    <span className='font-medium text-text-primary'>
                        {name}
                    </span>
                    <span className='text-text-secondary' aria-hidden='true'>
                        ·
                    </span>
                    <span className='inline-flex items-center gap-1 text-text-secondary'>
                        <ClockIcon className='size-3.5' aria-hidden='true' />
                        {date}
                    </span>
                </span>
                <span className='text-base text-text-primary'>
                    {message}
                </span>
            </span>
            <Button variant='outline' size='sm' aria-label={`Reply to ${name}`}>
                Reply
            </Button>
        </div>
    )
}

interface DrawerListSectionProps {
    title: string
    icon?: ReactNode
    count: ReactNode
    countIcon?: ReactNode
    defaultOpen?: boolean
    children?: ReactNode
}

/* A collapsible list inside a drawer: title, count badge and a caret. */
export function DrawerListSection({ title, icon, count, countIcon, defaultOpen = true, children }: DrawerListSectionProps) {
    const [open, setOpen] = useState(defaultOpen)

    return (
        <section className='flex flex-col'>
            <button
                type='button'
                onClick={() => setOpen(!open)}
                aria-expanded={open}
                className='flex min-h-12 w-full cursor-pointer items-center gap-2 rounded-md px-1 text-left outline-none hover:bg-surface-subtle focus-visible:outline-2 focus-visible:outline-border-focus'
            >
                {icon && (
                    <span className='inline-flex text-icon-secondary [&_svg]:size-4' aria-hidden='true'>
                        {icon}
                    </span>
                )}
                <span className='flex-1 text-base text-text-primary'>
                    {title}
                </span>
                <MetricBadgeCount value={count} icon={countIcon ?? <UserIcon className='text-icon-secondary' aria-hidden='true' />} className='text-sm' />
                <CaretDownIcon className={cn('size-4 text-icon-secondary transition-transform', open && 'rotate-180')} aria-hidden='true' />
            </button>
            {open && children && (
                <div className='flex flex-col'>
                    {children}
                </div>
            )}
        </section>
    )
}

/* PreviewRegion: stays empty until a real product preview is added. */
export function PreviewRegion({ className, label = 'Page preview', ...props }: ComponentProps<'div'> & { label?: string }) {
    return (
        <div
            data-slot='preview-region'
            role='img'
            aria-label={label}
            className={cn('min-h-48 rounded-lg border border-border-subtle bg-surface-subtle dark:bg-surface-page', className)}
            {...props}
        />
    )
}

interface CommentItem {
    author: string
    time: string
    text: string
}

/* Activity/Comment */
export function ActivityComment({ author, time, text }: CommentItem) {
    return (
        <div data-slot='activity-comment' className='flex items-start gap-3'>
            <Avatar size='default'>
                <AvatarImage src={avatarPhoto(author)} alt='' />
                <AvatarFallback />
            </Avatar>
            <div className='flex min-w-0 flex-col gap-0.5'>
                <span className='text-xs text-text-secondary'>
                    {author}
                    {' · '}
                    {time}
                </span>
                <p className='text-base text-text-primary'>
                    {text}
                </p>
            </div>
        </div>
    )
}

/* CommentComposer: avatar, comment input and a send action. */
export function CommentComposer({ onSubmit }: { onSubmit: (text: string) => void }) {
    const [value, setValue] = useState('')

    const submit = () => {
        const text = value.trim()
        if (!text) return
        onSubmit(text)
        setValue('')
    }

    return (
        <form
            data-slot='comment-composer'
            className='flex items-center gap-3'
            onSubmit={event => {
                event.preventDefault()
                submit()
            }}
        >
            <Avatar size='default'>
                <AvatarImage src={avatarPhoto('you')} alt='' />
                <AvatarFallback />
            </Avatar>
            <Input value={value} onChange={event => setValue(event.target.value)} placeholder='Add a comment...' aria-label='Add a comment' className='flex-1' />
            <Button type='submit' variant='ghost' size='icon' aria-label='Send comment' disabled={!value.trim()}>
                <PaperPlaneRightIcon aria-hidden='true' />
            </Button>
        </form>
    )
}

/* Footer recipe: one full-width Export data action. */
export function ExportFooterAction() {
    return (
        <Button className='w-full' size='lg'>
            <DownloadSimpleIcon aria-hidden='true' />
            Export data
        </Button>
    )
}

const REVIEW_STATES = ['Awaiting review', 'Approved', 'Building in progress', 'Launched']

/* Footer recipe: Actions label with a status select. */
export function ActionsFooterSelect({ value, onValueChange }: { value: string, onValueChange: (value: string) => void }) {
    return (
        <div className='flex w-full items-center justify-between gap-4'>
            <span className='text-base font-medium text-text-primary'>
                Actions
            </span>
            <Select value={value} onValueChange={next => onValueChange(String(next))}>
                <SelectTrigger className='w-auto min-w-44' aria-label='Action status'>
                    <SelectValue />
                </SelectTrigger>
                <SelectContent align='end'>
                    {REVIEW_STATES.map(state => (
                        <SelectItem key={state} value={state}>
                            {state}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
        </div>
    )
}

const FUNDRAISERS: SupporterRowProps[] = [
    { name: 'Oliver Wilson', amount: '$2,200', detail: '5 donors', rows: [{ name: 'Mia Clark', amount: '$1,100', share: '50%' }, { name: 'Lucas Hall', amount: '$660', share: '30%', connected: false }, { name: 'Ella Young', amount: '$440', share: '20%' }] },
    { name: 'Sophia Davis', amount: '$1,800', detail: '4 donors', rows: [{ name: 'James Lee', amount: '$900', share: '50%' }, { name: 'Grace Hill', amount: '$900', share: '50%', connected: false }] },
    { name: 'Emma Johnson', amount: '$1,245', detail: '3 donors', rows: [{ name: 'Henry King', amount: '$745', share: '60%' }, { name: 'Zoe Wright', amount: '$500', share: '40%' }] },
    {
        name: 'Noah Smith',
        amount: '$1,000',
        detail: '4 donors',
        defaultOpen: true,
        rows: [
            { name: 'Noah Smith', amount: '$400', share: '40%' },
            { name: 'Emma Johnson', amount: '$300', share: '30%', connected: false },
            { name: 'Liam Brown', amount: '$200', share: '20%', connected: false },
            { name: 'Ava Davis', amount: '$100', share: '10%', connected: false }
        ]
    },
    { name: 'Liam Brown', amount: '$500', detail: '1 donor' }
]

const REVENUE_SOURCES: { key: string, label: string, series: ChartSeries, value: number }[] = [
    { key: 'challenges', label: 'Challenges', series: 'challenges', value: 11000 },
    { key: 'instagram', label: 'Instagram', series: 'instagram', value: 8000 },
    { key: 'other', label: 'Other sources', series: 'other', value: 6000 },
    { key: 'general', label: 'General fundraisers', series: 'facebook', value: 24000 },
    { key: 'birthdays', label: 'Birthdays', series: 'birthdays', value: 5000 }
]

const matches = (text: string, query: string) => text.toLowerCase().includes(query.trim().toLowerCase())

function NoResults({ query }: { query: string }) {
    return (
        <p className='rounded-lg bg-surface-subtle px-4 py-6 text-center text-sm text-text-secondary'>
            {`Nothing matches "${query}".`}
        </p>
    )
}

/* Recipe: Revenue · People. Revenue by fundraiser with a Breakdown by source. */
export function RevenuePeopleContent({ defaultTab = 'people' }: { defaultTab?: 'people' | 'breakdown' }) {
    const [query, setQuery] = useState('')
    const [sourceQuery, setSourceQuery] = useState('')
    const fundraisers = FUNDRAISERS.filter(row => matches(row.name, query))
    const sources = REVENUE_SOURCES.filter(source => matches(source.label, sourceQuery))
    const total = REVENUE_SOURCES.reduce((sum, source) => sum + source.value, 0)

    return (
        <Tabs defaultValue={defaultTab} className='flex flex-col gap-6'>
            <TabsList fullWidth>
                <TabsTrigger value='people'>
                    People
                </TabsTrigger>
                <TabsTrigger value='breakdown'>
                    Breakdown
                </TabsTrigger>
            </TabsList>
            <TabsContent value='people' className='flex flex-col gap-4'>
                <DrawerMetric label='Revenue' value='$54,000' />
                <div className='grid grid-cols-3 gap-3'>
                    <DrawerMetric label='Fundraisers' value='7' />
                    <DrawerMetric label='Donors' value='28' />
                    <DrawerMetric label='Subscribers' value='321' />
                </div>
                <InputGroupSearch placeholder='Search fundraisers' value={query} onChange={event => setQuery(event.target.value)} />
                <DrawerListSection title='Fundraisers' count='7'>
                    {fundraisers.length
                        ? fundraisers.map(row => <SupporterRow key={row.name} {...row} />)
                        : <NoResults query={query} />}
                    <p className='pt-3 text-sm text-text-secondary'>
                        {`Showing ${fundraisers.length} of 7 fundraisers`}
                    </p>
                </DrawerListSection>
                <DrawerListSection title='Direct donors' count='11' defaultOpen={false}>
                    <SupporterRow name='Isabella Moore' amount='$250' detail='Direct donation' />
                    <SupporterRow name='Mason Taylor' amount='$180' detail='Direct donation' connected={false} />
                </DrawerListSection>
            </TabsContent>
            <TabsContent value='breakdown' className='flex flex-col gap-6'>
                <InputGroupSearch placeholder='Search revenue sources' value={sourceQuery} onChange={event => setSourceQuery(event.target.value)} />
                <div className='flex flex-col gap-1'>
                    <h3 className='font-heading text-lg font-semibold text-text-primary'>
                        Revenue by source
                    </h3>
                    <p className='text-sm text-text-secondary'>
                        January 1–31, 2026 · $54,000
                    </p>
                </div>
                <div className='flex flex-col gap-3'>
                    <StackedTrack
                        showAxis={false}
                        segments={REVENUE_SOURCES.map(source => ({
                            series: source.series,
                            value: source.value,
                            label: source.label,
                            icon: (
                                <span className='text-xs font-medium'>
                                    {`$${source.value / 1000}k`}
                                </span>
                            )
                        }))}
                    />
                    <div className='flex justify-between'>
                        <AxisLabel>$0</AxisLabel>
                        <AxisLabel>$54,000</AxisLabel>
                    </div>
                </div>
                <div className='flex flex-col gap-3'>
                    {sources.length
                        ? sources.map(source => (
                            <LegendItem
                                key={source.key}
                                source={sourceKey(source.key)}
                                series={source.series}
                                label={source.label}
                                value={`$${source.value.toLocaleString('en-US')}`}
                                share={`${(source.value / total * 100).toFixed(1)}%`}
                            />
                        ))
                        : <NoResults query={sourceQuery} />}
                </div>
                <ChartCardFooter summary='$54,000 across five revenue sources' period='Matches the January column in the Revenue chart.' />
            </TabsContent>
        </Tabs>
    )
}

const MESSAGES: MessageRowProps[] = [
    { name: 'David', date: 'Jan 2', message: "Let's set up a meeting to discuss further." },
    { name: 'Jessica', date: 'Jan 6', message: 'I have some ideas that could help.' },
    { name: 'Michael', date: 'Jan 7', message: "I'm interested in collaborating on this." },
    { name: 'Sarah', date: 'Jan 7', message: 'Great insights on the project.' },
    { name: 'Brandon', date: 'Jan 9', message: 'Would love to explore how we could help.' },
    { name: 'Oliver', date: 'Jan 21', message: 'Thank you for sharing this with us.' }
]

/* Recipe: Facebook Direct. The people behind one source, with messages awaiting a reply. */
export function FacebookDirectContent() {
    const [query, setQuery] = useState('')
    const messages = MESSAGES.filter(item => matches(`${item.name} ${item.message}`, query))

    return (
        <div className='flex flex-col gap-4'>
            <div className='grid grid-cols-2 gap-3'>
                <DrawerMetric label='New subscribers' value='640' />
                <DrawerMetric label='New supporters' value='3,000' />
            </div>
            <InputGroupSearch placeholder='Search supporters' value={query} onChange={event => setQuery(event.target.value)} />
            <DrawerListSection title='Awaiting reply' icon={<CrownIcon />} count='6' countIcon={<UserMessengerIcon className='text-external-messenger' />}>
                {messages.length ? messages.map(item => <MessageRow key={item.name} {...item} />) : <NoResults query={query} />}
            </DrawerListSection>
            <DrawerListSection title='All other subscribers' icon={<UserIcon />} count='634' countIcon={<UserMessengerIcon className='text-external-messenger' />} defaultOpen={false}>
                <SupporterRow name='Charlotte Green' amount='$75' detail='Subscribed Jan 4' />
                <SupporterRow name='Benjamin Scott' amount='$40' detail='Subscribed Jan 12' />
            </DrawerListSection>
        </div>
    )
}

const channelGroups: Record<string, SourceData['groups']> = {
    facebook: [
        { id: 'jan-facebook-post', label: 'Giving Tuesday post', supporters: { value: '64', detail: '53%' } },
        { id: 'jan-facebook-birthday', label: 'Birthday fundraiser prompt', supporters: { value: '56', detail: '47%' } }
    ],
    messenger: [
        { id: 'jan-messenger-lookalike', label: 'Lookalike audience', supporters: { value: '48', detail: '60%' } },
        { id: 'jan-messenger-retargeting', label: 'Retargeting', supporters: { value: '32', detail: '40%' } }
    ],
    platform: [
        { id: 'jan-platform-grow', label: 'Grow page', supporters: { value: '30', detail: '60%' } },
        { id: 'jan-platform-signup', label: 'Signup form', supporters: { value: '20', detail: '40%' } }
    ],
    challenges: [
        { id: 'jan-challenges-run', label: '100 Mile Run', supporters: { value: '62', detail: '62%' } },
        { id: 'jan-challenges-push', label: '50 Push ups', supporters: { value: '38', detail: '38%' } }
    ]
}

/* Recipe: January 2026. New supporters by acquisition channel. */
export function AcquisitionMonthContent({ defaultTab = 'breakdown' }: { defaultTab?: 'people' | 'breakdown' }) {
    const [query, setQuery] = useState('')
    const channels = ACQUISITION_CHANNELS.filter(channel => matches(channel.label, query))

    return (
        <Tabs defaultValue={defaultTab} className='flex flex-col gap-6'>
            <TabsList fullWidth>
                <TabsTrigger value='people'>
                    People
                </TabsTrigger>
                <TabsTrigger value='breakdown'>
                    Breakdown
                </TabsTrigger>
            </TabsList>
            <TabsContent value='people' className='flex flex-col gap-4'>
                <DrawerMetric label='New supporters' value='350' people={5} />
                <DrawerListSection title='Joined in January' count='350'>
                    <SupporterRow name='Amelia Turner' amount='$120' detail='Facebook Direct' />
                    <SupporterRow name='Ethan Walker' amount='$60' detail='Messenger Ads' />
                    <SupporterRow name='Harper Lewis' amount='$45' detail='Challenges' connected={false} />
                    <SupporterRow name='Jack Robinson' amount='$30' detail='Platform' />
                </DrawerListSection>
            </TabsContent>
            <TabsContent value='breakdown' className='flex flex-col gap-6'>
                <InputGroupSearch placeholder='Search acquisition channels' value={query} onChange={event => setQuery(event.target.value)} />
                <div className='flex flex-col gap-1'>
                    <h3 className='font-heading text-lg font-semibold text-text-primary'>
                        Acquisition channels
                    </h3>
                    <p className='text-sm text-text-secondary'>
                        New supporters · January 2026
                    </p>
                </div>
                <StackedTrack
                    segments={ACQUISITION_CHANNELS.map(channel => ({
                        series: channel.series,
                        value: channel.value,
                        label: channel.label,
                        icon: <SourceIcon source={channel.id} />
                    }))}
                />
                {channels.length
                    ? (
                        <SourceList
                            sources={channels.map(channel => ({
                                id: channel.id,
                                label: channel.label,
                                accent: channel.series,
                                icon: <SourceIcon source={channel.id} />,
                                supporters: { value: String(channel.value), detail: channel.share },
                                groups: channelGroups[channel.id]
                            }))}
                        />
                    )
                    : <NoResults query={query} />}
                <ChartCardFooter summary='350 supporters across four channels' period='Each segment shows its share of the same total.' />
            </TabsContent>
        </Tabs>
    )
}

const NONPROFITS = ['National Kidney Foundation', 'American Heart Association', 'Feeding America']
const OUTCOMES = ['World Kidney Day Awareness', 'Monthly giving', 'Volunteer signups']

const INITIAL_COMMENTS: CommentItem[] = [
    { author: 'Adelina', time: '2 minutes ago', text: 'The page is ready for review.' }
]

interface EditorValues {
    nonprofit: string
    outcome: string
    purpose: string
    pageName: string
    headline: string
    body: string
}

const EDITOR_DEFAULTS: EditorValues = {
    nonprofit: 'National Kidney Foundation',
    outcome: 'World Kidney Day Awareness',
    purpose: '',
    pageName: 'World Kidney Day',
    headline: 'Support kidney health',
    body: ''
}

interface EditorContentProps {
    defaultValues?: Partial<EditorValues>
    /* Increase after saving: the current values become the new baseline for unsaved edits. */
    saveSignal?: number
    onDirtyChange?: (dirty: boolean) => void
}

/* Recipe: Editor. Fundraiser page fields, page sections, preview and comments. Reports unsaved edits. */
export function EditorContent({ defaultValues, saveSignal = 0, onDirtyChange }: EditorContentProps) {
    const [values, setValues] = useState<EditorValues>({ ...EDITOR_DEFAULTS, ...defaultValues })
    const [comments, setComments] = useState<CommentItem[]>(INITIAL_COMMENTS)
    const baseline = useRef<EditorValues>(values)
    const latest = useRef<EditorValues>(values)
    const notify = useRef(onDirtyChange)

    useEffect(() => {
        notify.current = onDirtyChange
    }, [onDirtyChange])

    useEffect(() => {
        if (!saveSignal) return
        baseline.current = latest.current
        notify.current?.(false)
    }, [saveSignal])

    const update = (key: keyof EditorValues, value: string) => {
        const next = { ...values, [key]: value }
        latest.current = next
        setValues(next)
        onDirtyChange?.((Object.keys(next) as (keyof EditorValues)[]).some(field => next[field] !== baseline.current[field]))
    }

    return (
        <div className='flex flex-col gap-6'>
            <div className='flex flex-col gap-4'>
                <DrawerFieldRow label='Type'>
                    <Badge variant='info' className='mt-2'>
                        Fundraiser Page
                    </Badge>
                </DrawerFieldRow>
                <DrawerFieldRow label='Nonprofit'>
                    <Select value={values.nonprofit} onValueChange={value => update('nonprofit', String(value))}>
                        <SelectTrigger aria-label='Nonprofit'>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {NONPROFITS.map(item => (
                                <SelectItem key={item} value={item}>
                                    {item}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </DrawerFieldRow>
                <DrawerFieldRow label='Outcome'>
                    <Select value={values.outcome} onValueChange={value => update('outcome', String(value))}>
                        <SelectTrigger aria-label='Outcome'>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {OUTCOMES.map(item => (
                                <SelectItem key={item} value={item}>
                                    {item}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </DrawerFieldRow>
                <DrawerFieldRow label='Purpose' htmlFor='editor-purpose'>
                    <Textarea
                        id='editor-purpose'
                        value={values.purpose}
                        onChange={event => update('purpose', event.target.value)}
                        placeholder='Describe the goal of this campaign and what you want supporters to do.'
                        className='min-h-24'
                    />
                </DrawerFieldRow>
                <DrawerFieldRow label='Page name' htmlFor='editor-page-name'>
                    <Input id='editor-page-name' value={values.pageName} onChange={event => update('pageName', event.target.value)} />
                </DrawerFieldRow>
            </div>
            <Separator />
            <Tabs defaultValue='landing' className='flex flex-col gap-4'>
                <TabsList fullWidth>
                    <TabsTrigger value='landing'>
                        Landing
                    </TabsTrigger>
                    <TabsTrigger value='fundraising'>
                        Fundraising
                    </TabsTrigger>
                    <TabsTrigger value='success'>
                        Success page
                    </TabsTrigger>
                </TabsList>
                <TabsContent value='landing' className='flex flex-col gap-4'>
                    <DrawerFieldRow label='Headline' htmlFor='editor-headline'>
                        <Input id='editor-headline' value={values.headline} onChange={event => update('headline', event.target.value)} />
                    </DrawerFieldRow>
                    <DrawerFieldRow label='Body text' htmlFor='editor-body'>
                        <Textarea
                            id='editor-body'
                            value={values.body}
                            onChange={event => update('body', event.target.value)}
                            placeholder='Help more families get the support they need.'
                            className='min-h-24'
                        />
                    </DrawerFieldRow>
                </TabsContent>
                <TabsContent value='fundraising' className='flex flex-col gap-4'>
                    <DrawerFieldRow label='Goal' htmlFor='editor-goal'>
                        <Input id='editor-goal' placeholder='$10,000' />
                    </DrawerFieldRow>
                    <DrawerFieldRow label='Deadline' htmlFor='editor-deadline'>
                        <Input id='editor-deadline' placeholder='March 12, 2026' />
                    </DrawerFieldRow>
                </TabsContent>
                <TabsContent value='success' className='flex flex-col gap-4'>
                    <DrawerFieldRow label='Thank you' htmlFor='editor-thanks'>
                        <Textarea id='editor-thanks' placeholder='Thank you for standing with families living with kidney disease.' className='min-h-24' />
                    </DrawerFieldRow>
                </TabsContent>
            </Tabs>
            <section className='flex flex-col gap-3'>
                <h3 className='font-heading text-lg font-semibold text-text-primary'>
                    Preview
                </h3>
                <PreviewRegion />
            </section>
            <Tabs defaultValue='comments' className='flex flex-col gap-4'>
                <TabsList fullWidth>
                    <TabsTrigger value='comments'>
                        Comments
                    </TabsTrigger>
                    <TabsTrigger value='activity'>
                        All activity
                    </TabsTrigger>
                </TabsList>
                <TabsContent value='comments' className='flex flex-col gap-4'>
                    {comments.map((comment, index) => <ActivityComment key={`${comment.author}-${index}`} {...comment} />)}
                    <CommentComposer onSubmit={text => setComments(current => [...current, { author: 'You', time: 'just now', text }])} />
                </TabsContent>
                <TabsContent value='activity' className='flex flex-col gap-4'>
                    <ActivityComment author='GoodUnited' time='Yesterday' text='Page created from the World Kidney Day outcome.' />
                    {comments.map((comment, index) => <ActivityComment key={`${comment.author}-activity-${index}`} {...comment} />)}
                </TabsContent>
            </Tabs>
        </div>
    )
}

export type { CommentItem, EditorValues, MessageRowProps, SupporterChild, SupporterRowProps }
